import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Rocket, Gift, Users, DollarSign, Trophy, Calendar,
  TrendingUp, Target, CheckCircle, Clock, ArrowRight,
  Plus, Loader2, Camera, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const FounderDashboard = () => {
  const { token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Form state for new giveaway
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_type: '',
    value: '',
    image_url: '',
    city: '',
    province_state: '',
    end_date: ''
  });

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/founder/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch dashboard');
      
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      toast.error('Failed to load founder dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [token]);

  const handleCreateGiveaway = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.value || !formData.city || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setCreating(true);
      const response = await fetch(`${API}/api/founder/giveaways`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          value: parseFloat(formData.value),
          end_date: new Date(formData.end_date).toISOString()
        })
      });

      if (!response.ok) throw new Error('Failed to create giveaway');

      toast.success('Founder giveaway created!');
      setCreateDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        project_type: '',
        value: '',
        image_url: '',
        city: '',
        province_state: '',
        end_date: ''
      });
      fetchDashboard();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  const { metrics, day1_liquidity_status, founder_giveaways } = dashboardData || {};

  return (
    <div className="space-y-8" data-testid="founder-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Rocket className="w-6 h-6 text-[#FF3B30]" />
            Day 1 Liquidity Dashboard
          </h1>
          <p className="text-neutral-500 mt-1">
            Your first 3-5 giveaways build the proof that makes everything else work
          </p>
        </div>
        <Button 
          onClick={() => setCreateDialogOpen(true)}
          className="bg-[#FF3B30] hover:bg-[#DC2626]"
          data-testid="create-founder-giveaway-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Founder Giveaway
        </Button>
      </div>

      {/* Progress Card */}
      <div className="bg-gradient-to-r from-[#FF3B30] to-[#DC2626] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Target className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Launch Progress</h2>
            <p className="text-white/80 text-sm">
              {day1_liquidity_status?.completed_giveaways || 0} of {day1_liquidity_status?.giveaways_needed || 5} giveaways completed
            </p>
          </div>
        </div>
        
        <div className="mb-4">
          <Progress 
            value={day1_liquidity_status?.progress_percent || 0} 
            className="h-3 bg-white/20"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {day1_liquidity_status?.ready_for_scale ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-300" />
                <span className="font-medium">Ready to scale!</span>
              </>
            ) : (
              <>
                <Clock className="w-5 h-5 text-yellow-300" />
                <span className="font-medium">
                  {(day1_liquidity_status?.giveaways_needed || 5) - (day1_liquidity_status?.completed_giveaways || 0)} more to go
                </span>
              </>
            )}
          </div>
          <Sparkles className="w-6 h-6 text-yellow-300" />
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={Gift}
          label="Total Giveaways"
          value={metrics?.total_giveaways || 0}
          color="bg-purple-100 text-purple-600"
        />
        <MetricCard
          icon={CheckCircle}
          label="Completed"
          value={metrics?.completed_giveaways || 0}
          color="bg-green-100 text-green-600"
        />
        <MetricCard
          icon={Users}
          label="Total Entries"
          value={metrics?.total_entries || 0}
          color="bg-blue-100 text-blue-600"
        />
        <MetricCard
          icon={DollarSign}
          label="Value Given"
          value={`$${(metrics?.total_value_given || 0).toLocaleString()}`}
          color="bg-emerald-100 text-emerald-600"
        />
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Past Clients Reached</p>
              <p className="text-2xl font-bold text-neutral-900">{metrics?.past_clients_reached || 0}</p>
            </div>
          </div>
          <p className="text-sm text-neutral-500">
            Homeowners who have entered your founder giveaways
          </p>
        </div>
        
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Content Pieces Created</p>
              <p className="text-2xl font-bold text-neutral-900">{metrics?.content_pieces_created || 0}</p>
            </div>
          </div>
          <p className="text-sm text-neutral-500">
            Before/after photos and testimonials collected
          </p>
        </div>
      </div>

      {/* Founder Giveaways List */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="font-semibold text-neutral-900">Your Founder Giveaways</h3>
          <span className="text-sm text-neutral-500">{founder_giveaways?.length || 0} giveaways</span>
        </div>
        
        {!founder_giveaways || founder_giveaways.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
            <Gift className="w-12 h-12 mb-4 opacity-50" />
            <p className="mb-4">No founder giveaways yet</p>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-[#FF3B30] hover:bg-[#DC2626]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Giveaway
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {founder_giveaways.map((giveaway) => (
              <div 
                key={giveaway.giveaway_id} 
                className="p-4 hover:bg-neutral-50 flex items-center gap-4"
                data-testid={`founder-giveaway-${giveaway.giveaway_id}`}
              >
                <img 
                  src={giveaway.image_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100'} 
                  alt={giveaway.title}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-neutral-900 truncate">{giveaway.title}</p>
                  <p className="text-sm text-neutral-500">{giveaway.city}, {giveaway.province_state}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">${giveaway.value?.toLocaleString()}</p>
                  <p className="text-sm text-neutral-500">{giveaway.total_entries || 0} entries</p>
                </div>
                <div className="flex items-center gap-2">
                  {giveaway.winner_id ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      Winner Selected
                    </span>
                  ) : giveaway.status === 'active' ? (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {giveaway.status}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Why Day 1 Matters */}
      <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-2xl p-6 text-white">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#FF3B30]" />
          Why Day 1 Liquidity Matters
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="w-8 h-8 bg-[#FF3B30] rounded-lg flex items-center justify-center mb-3">
              <span className="font-bold">1</span>
            </div>
            <h4 className="font-medium mb-1">Build Proof</h4>
            <p className="text-sm text-neutral-400">
              Real giveaways with real winners create the social proof sponsors need
            </p>
          </div>
          <div>
            <div className="w-8 h-8 bg-[#FF3B30] rounded-lg flex items-center justify-center mb-3">
              <span className="font-bold">2</span>
            </div>
            <h4 className="font-medium mb-1">Collect Content</h4>
            <p className="text-sm text-neutral-400">
              Before/after photos and testimonials become your best marketing assets
            </p>
          </div>
          <div>
            <div className="w-8 h-8 bg-[#FF3B30] rounded-lg flex items-center justify-center mb-3">
              <span className="font-bold">3</span>
            </div>
            <h4 className="font-medium mb-1">Attract Sponsors</h4>
            <p className="text-sm text-neutral-400">
              Success stories bring in sponsors who fund the next round of giveaways
            </p>
          </div>
        </div>
      </div>

      {/* Create Giveaway Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Founder Giveaway</DialogTitle>
            <DialogDescription>
              Launch your first giveaways to build proof and attract sponsors
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateGiveaway} className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Kitchen Cabinet Refinishing"
                data-testid="founder-giveaway-title"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the giveaway..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="value">Value ($) *</Label>
                <Input
                  id="value"
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                  placeholder="5000"
                  data-testid="founder-giveaway-value"
                />
              </div>
              <div>
                <Label htmlFor="project_type">Project Type</Label>
                <Select 
                  value={formData.project_type} 
                  onValueChange={(v) => setFormData({...formData, project_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interior_painting">Interior Painting</SelectItem>
                    <SelectItem value="exterior_painting">Exterior Painting</SelectItem>
                    <SelectItem value="flooring">Flooring</SelectItem>
                    <SelectItem value="kitchen_cabinet_refinishing">Cabinet Refinishing</SelectItem>
                    <SelectItem value="bathroom_renovation">Bathroom Renovation</SelectItem>
                    <SelectItem value="cleaning">Deep Cleaning</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Toronto"
                  data-testid="founder-giveaway-city"
                />
              </div>
              <div>
                <Label htmlFor="province_state">Province/State</Label>
                <Input
                  id="province_state"
                  value={formData.province_state}
                  onChange={(e) => setFormData({...formData, province_state: e.target.value})}
                  placeholder="Ontario"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="end_date">End Date *</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                data-testid="founder-giveaway-end-date"
              />
            </div>
            
            <div>
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={creating}
                className="bg-[#FF3B30] hover:bg-[#DC2626]"
                data-testid="submit-founder-giveaway"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Giveaway'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-neutral-200 p-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="text-2xl font-bold text-neutral-900">{value}</p>
    <p className="text-sm text-neutral-500">{label}</p>
  </div>
);

export default FounderDashboard;
